// 제품중량 계산 팝업 — 형태별(원형/육각/사각) 다중 행 표로 제품 합계중량(g)을 산출한다.
// 계산은 @/lib/cost/weightCalc 의 순수 함수(productRowWeight, productTotalWeight)를 재사용.
import { Icon, Select, TextInput } from "@/components/ui";
import type { CncMasterData } from "@/lib/cost/cncForm";
import {
  type ProductShape,
  productRowWeight,
  productTotalWeight,
  resolveRowDensity,
  withDefaultGeometry,
} from "@/lib/cost/weightCalc";
import type { CncProductWeightRow } from "@/lib/data";
import { useState } from "react";

const fmtDec = (n: number, d = 2): string =>
  Number.isFinite(n) ? n.toLocaleString("ko-KR", { maximumFractionDigits: d }) : "—";

const num = (s: string): number => Number.parseFloat(s) || 0;

const SHAPE_OPTS: { value: ProductShape; label: string }[] = [
  { value: "round", label: "원형 (환봉)" },
  { value: "hex", label: "육각" },
  { value: "rect", label: "사각" },
];

const th = {
  padding: "8px 10px",
  textAlign: "left" as const,
  fontSize: 11,
  whiteSpace: "nowrap" as const,
};
const thR = { ...th, textAlign: "right" as const };
const cell = { padding: "6px 8px", borderTop: "1px solid var(--bg-surface-2)" };
const cellR = { ...cell, textAlign: "right" as const, fontFamily: "monospace" };

export const ProductWeightCalcModal = ({
  master,
  materialId,
  initialRows,
  initialOuterD,
  initialLength,
  onClose,
  onApply,
}: {
  master: CncMasterData;
  materialId: string;
  initialRows?: CncProductWeightRow[];
  // 투입중량 계산에서 입력한 기하 — 저장된 행이 없을 때 첫 행 외경·길이 프리필(#92).
  initialOuterD?: string;
  initialLength?: string;
  onClose: () => void;
  onApply: (r: { productWeight: string; rows: CncProductWeightRow[] }) => void;
}) => {
  // 투입중량 계산에서 고른 재질 — 빈 규격명 행의 기본값(자동 연동) + 단중 fallback 의 원천.
  const material = master.raw.find((r) => r.id === materialId);
  const materialName = material?.name ?? "";
  const materialGPerMm3 = material?.gPerMm3 ?? 0;

  // 규격명(spec) 드롭다운 = 재질 마스터 이름 목록. spec 에는 name 을 저장한다(라운드트립).
  const specOptions = master.raw.map((m) => ({ value: m.name, label: m.name }));

  // 새 행 규격명 기본값 = 투입중량 재질명(자동 연동). 단중은 비워 둔다(규격명을 따름).
  const emptyRow = (): CncProductWeightRow => ({
    shape: "round",
    spec: materialName,
    outer: "",
    inner: "0",
    length: "",
    gPerMm3: "",
    qty: "1",
  });

  // 저장된 행을 열 때 — 규격명이 비어 있으면 투입중량 재질로 기본 연동(레거시 텍스트는 그대로 둠).
  const withDefaultSpec = (r: CncProductWeightRow): CncProductWeightRow =>
    r.spec.trim() ? r : { ...r, spec: materialName };

  // 저장된 행이 없는 신규 열기 — 첫 행 외경·길이를 투입중량 기하로 프리필(#92).
  // 규격명·단중은 emptyRow 가 이미 투입중량 재질로 연동(#90). 외경/길이만 비어 있어
  // 제품중량이 0 이던 문제를 해소한다. 채워진 값은 수정 가능(가공 변형 반영).
  const freshRow = (): CncProductWeightRow =>
    withDefaultGeometry(emptyRow(), initialOuterD ?? "", initialLength ?? "");

  const [rows, setRows] = useState<CncProductWeightRow[]>(
    initialRows?.length ? initialRows.map(withDefaultSpec) : [freshRow()]
  );

  // 레거시 호환 — 저장된 규격명이 재질 마스터에 없는 자유 텍스트면 그 값을 옵션으로 추가해
  // 드롭다운에서 그대로 보이고 선택을 유지하게 한다(크래시/소실 방지).
  const specOptionsFor = (spec: string) => {
    const key = spec.trim();
    if (!key || specOptions.some((o) => o.value === key)) return specOptions;
    return [...specOptions, { value: key, label: key }];
  };

  const toCalcRow = (r: CncProductWeightRow) => ({
    shape: r.shape,
    outer: num(r.outer),
    inner: num(r.inner),
    length: num(r.length),
    // 단중은 행별 규격명(재질) 조회값을 따른다 — 직접 입력 시 우선, 미매칭 시 투입중량 재질로 fallback.
    gPerMm3: resolveRowDensity(num(r.gPerMm3), r.spec, master.raw, materialGPerMm3),
    qty: num(r.qty),
  });

  const total = productTotalWeight(rows.map(toCalcRow));

  const setRow = (idx: number, key: keyof CncProductWeightRow, v: string) =>
    setRows(rows.map((r, i) => (i === idx ? { ...r, [key]: v } : r)));
  const addRow = () => setRows([...rows, emptyRow()]);
  const removeRow = (idx: number) =>
    setRows(rows.length > 1 ? rows.filter((_, i) => i !== idx) : rows);

  const apply = () => {
    // 소수 부동소수점 잡음 제거 — 4자리로 반올림.
    onApply({ productWeight: total ? String(Math.round(total * 1e4) / 1e4) : "", rows });
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 1040,
          maxWidth: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "var(--bg-surface)",
          borderRadius: 14,
          boxShadow: "rgba(0,0,0,0.5) 0px 8px 24px",
          padding: 24,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <Icon name="grid" size={18} color="var(--brand)" />
          <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 18 }}>
            제품 중량 계산
          </div>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--text-dim)",
              cursor: "pointer",
              display: "flex",
            }}
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        {/* 형태별 중량 계산 공식 */}
        <div
          style={{
            background: "var(--bg-surface-2)",
            borderRadius: 10,
            padding: "12px 16px",
            fontSize: 12,
            color: "var(--text-muted)",
            marginBottom: 16,
            lineHeight: 1.7,
          }}
        >
          <b>형태별 중량 계산</b> (내경이 있으면 내경분 제외, 원주율 3.142 고정) — 중량(g) = 단면적
          × 길이 × 단중
          <br />· 원형: π(외경/2)² − π(내경/2)² &nbsp;·&nbsp; 육각: (3√3/2)(외경/2)² −
          (3√3/2)(내경/2)² &nbsp;·&nbsp; 사각: (외경/가로)² − (내경)²
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead style={{ background: "var(--bg-card-hover)", color: "var(--text-dim)" }}>
              <tr>
                <th style={th}>형태</th>
                <th style={th}>규격명</th>
                <th style={thR}>외경·가로 (mm)</th>
                <th style={thR}>내경 (mm)</th>
                <th style={thR}>길이 (mm)</th>
                <th style={thR}>단중 (g/mm³)</th>
                <th style={thR}>수량 (EA)</th>
                <th style={thR}>단품중량 (g)</th>
                <th style={thR}>총중량 (g)</th>
                <th style={{ ...th, textAlign: "center" }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const w = productRowWeight(toCalcRow(r));
                // 미입력 단중 placeholder = 그 행 규격명으로 조회한 단중(행별 연동 명시).
                const autoDensity = resolveRowDensity(0, r.spec, master.raw, materialGPerMm3);
                return (
                  <tr key={idx}>
                    <td style={{ ...cell, minWidth: 130 }}>
                      <Select
                        value={r.shape}
                        onChange={(v) => setRow(idx, "shape", v as ProductShape)}
                        options={SHAPE_OPTS}
                        style={{ height: 36 }}
                      />
                    </td>
                    <td style={{ ...cell, minWidth: 130 }}>
                      <Select
                        value={r.spec}
                        onChange={(v) => setRow(idx, "spec", v)}
                        options={specOptionsFor(r.spec)}
                        placeholder="재질 선택"
                        style={{ height: 36 }}
                      />
                    </td>
                    <td style={{ ...cell, minWidth: 90 }}>
                      <TextInput
                        value={r.outer}
                        onChange={(v) => setRow(idx, "outer", v)}
                        type="number"
                        mono
                        align="right"
                        style={{ height: 36 }}
                      />
                    </td>
                    <td style={{ ...cell, minWidth: 80 }}>
                      <TextInput
                        value={r.inner}
                        onChange={(v) => setRow(idx, "inner", v)}
                        type="number"
                        mono
                        align="right"
                        style={{ height: 36 }}
                      />
                    </td>
                    <td style={{ ...cell, minWidth: 80 }}>
                      <TextInput
                        value={r.length}
                        onChange={(v) => setRow(idx, "length", v)}
                        type="number"
                        mono
                        align="right"
                        style={{ height: 36 }}
                      />
                    </td>
                    <td style={{ ...cell, minWidth: 100 }}>
                      <TextInput
                        value={r.gPerMm3}
                        onChange={(v) => setRow(idx, "gPerMm3", v)}
                        type="number"
                        mono
                        align="right"
                        // 미입력 시 그 행 규격명의 단중을 placeholder 로 보여 줘 행별 연동을 명시한다.
                        placeholder={autoDensity ? String(autoDensity) : undefined}
                        style={{ height: 36 }}
                      />
                    </td>
                    <td style={{ ...cell, minWidth: 70 }}>
                      <TextInput
                        value={r.qty}
                        onChange={(v) => setRow(idx, "qty", v)}
                        type="number"
                        mono
                        align="right"
                        style={{ height: 36 }}
                      />
                    </td>
                    <td style={cellR}>{fmtDec(w.unitWeight)}</td>
                    <td style={{ ...cellR, fontWeight: 700 }}>{fmtDec(w.totalWeight)}</td>
                    <td style={{ ...cell, textAlign: "center" }}>
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        title="행 삭제"
                        style={{
                          background: "transparent",
                          border: 0,
                          color: "var(--text-dim)",
                          cursor: "pointer",
                          display: "inline-flex",
                        }}
                      >
                        <Icon name="trash" size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: "var(--bg-card-hover)" }}>
                <td style={{ ...cell, fontWeight: 700 }} colSpan={8}>
                  합계 중량
                </td>
                <td style={{ ...cellR, fontWeight: 800, color: "var(--brand)", fontSize: 14 }}>
                  {fmtDec(total, 3)} g
                </td>
                <td style={cell} />
              </tr>
            </tfoot>
          </table>
        </div>

        <div style={{ display: "flex", alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={addRow}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 10,
              border: 0,
              background: "var(--bg-surface-2)",
              color: "var(--text-base)",
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
            }}
          >
            <Icon name="plus" size={15} /> 행 추가
          </button>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginRight: 16 }}>
            ℹ 제품 중량을 저장하면 견적 입력 화면에 자동 반영됩니다.
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "10px 20px",
              borderRadius: 12,
              border: 0,
              background: "var(--bg-surface-2)",
              color: "var(--text-base)",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 14,
              marginRight: 10,
            }}
          >
            취소
          </button>
          <button
            type="button"
            onClick={apply}
            style={{
              padding: "10px 24px",
              borderRadius: 12,
              border: 0,
              background: "var(--brand)",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 14,
            }}
          >
            적용
          </button>
        </div>
      </div>
    </div>
  );
};
